import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import type {MenuItem, MenuContent} from '@pexip/components';
import {Color, IconTypes, Menu, Scrollbars} from '@pexip/components';
import {TestId} from '../../../test/testIds';
import type {UserMenuStep} from '../../types';

export const UserMenu: React.FC<{
    hasMedia?: boolean;
    isDirectMedia?: boolean;
    isFeedbackEnabled?: boolean;
    isInMeeting: boolean;
    canChangeLayout?: boolean;
    canChangePersonalLayout?: boolean;
    showLeave?: boolean;
    showQuality?: boolean;
    showMediaStatistics?: boolean;
    pluginActions: MenuItem[];
    setUserMenuStep: (settings: UserMenuStep) => void;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    downloadLogWithName: () => void;
    openFeedbackForm: () => void;
    onLeave?: () => void;
}> = ({
    hasMedia,
    isDirectMedia,
    isFeedbackEnabled,
    isInMeeting,
    canChangeLayout,
    canChangePersonalLayout,
    showLeave = false,
    showQuality = true,
    showMediaStatistics = true,
    setUserMenuStep,
    pluginActions,
    close,
    downloadLogWithName,
    openFeedbackForm,
    onLeave,
}) => {
    const {t} = useTranslation();

    const menuContent: MenuContent = useMemo(() => {
        const menu = [
            ...(hasMedia
                ? [
                      {
                          label: t(
                              'settings.video-and-sound',
                              'Video and sound',
                          ),
                          clickAction: () => setUserMenuStep('video-and-sound'),
                          testId: TestId.ButtonMeetingDeviceSettings,
                          role: 'menuitem',
                      },
                  ]
                : []),
            {
                label: t('settings.joining', 'Joining'),
                clickAction: () => setUserMenuStep('joining'),
                testId: TestId.TabSettingsJoiningSettings,
                role: 'menuitem',
            },
            ...(showQuality
                ? [
                      {
                          label: t('settings.quality', 'Quality'),
                          clickAction: () => setUserMenuStep('quality'),
                          testId: TestId.LinkQuality,

                          role: 'menuitem',
                      },
                  ]
                : []),
            {
                label: t('settings.languages', 'Language'),
                clickAction: () => setUserMenuStep('language'),
                testId: TestId.LinkLanguages,
                role: 'menuitem',
            },
            ...(isInMeeting &&
            (Boolean(canChangeLayout) || Boolean(canChangePersonalLayout)) &&
            !isDirectMedia
                ? [
                      {
                          label: canChangeLayout
                              ? t('settings.meeting-layout', 'Meeting layout')
                              : t('settings.layout', 'Layout'),
                          clickAction: () => setUserMenuStep('layouts'),
                          testId: TestId.TabSettingsMeetingLayout,
                          role: 'menuitem',
                      },
                  ]
                : []),
            {
                label: t('settings.accessibility', 'Accessibility'),
                clickAction: () => setUserMenuStep('accessibility'),
                testId: TestId.ButtonAccessibilitySettings,
                role: 'menuitem',
            },
            {
                label: t('settings.advanced', 'Advanced'),
                clickAction: () => setUserMenuStep('advanced'),
                testId: TestId.TabSettingsAdvancedSettings,
                role: 'menuitem',
            },
            {
                label: t('settings.about', 'About'),
                clickAction: () => setUserMenuStep('about'),
                testId: TestId.LinkAboutPexip,
                role: 'menuitem',
            },
            ...(isFeedbackEnabled
                ? [
                      {
                          label: t('meeting.menu-feedback', 'Send feedback'),
                          testId: TestId.SubmitFeedback,
                          clickAction: (
                              e: React.SyntheticEvent<HTMLElement>,
                          ) => {
                              openFeedbackForm();
                              close(e);
                          },
                          icon: IconTypes.IconOpenInNew,
                          role: 'menuitem',
                      },
                  ]
                : []),
            ...(isInMeeting && showMediaStatistics
                ? [
                      {
                          label: t('stats.media-stats', 'Media statistics'),
                          clickAction: () => setUserMenuStep('stats'),
                          testId: TestId.ButtonSelfviewQuality,
                          role: 'menuitem',
                      },
                  ]
                : []),
            ...pluginActions,
            {
                label: t('common.download-log', 'Download log'),
                testId: TestId.LinkDownloadLog,
                clickAction: downloadLogWithName,
                role: 'menuitem',
            },
        ];

        if (showLeave) {
            return [
                menu,
                [
                    {
                        label: t('meeting.leave', 'Leave'),
                        testId: TestId.ButtonLeaveUserMenu,
                        clickAction: onLeave,
                        icon: IconTypes.IconPhoneHorisontal,
                        iconColor: Color.SignalRed,
                        isFixedIcon: true,
                        iconAlignedStart: true,
                        role: 'menuitem',
                        variant: 'danger',
                    },
                ],
            ];
        }

        return [menu];
    }, [
        hasMedia,
        t,
        isDirectMedia,
        isInMeeting,
        canChangeLayout,
        canChangePersonalLayout,
        pluginActions,
        isFeedbackEnabled,
        setUserMenuStep,
        openFeedbackForm,
        close,
        showLeave,
        onLeave,
        downloadLogWithName,
        showQuality,
        showMediaStatistics,
    ]);

    return (
        <div style={{maxHeight: 'inherit', display: 'flex'}}>
            <Scrollbars style={{minHeight: 0}}>
                <div>
                    <Menu testId={TestId.UserMenu} menuContent={menuContent} />
                </div>
            </Scrollbars>
        </div>
    );
};

export type UserMenuProps = React.ComponentProps<typeof UserMenu>;
